import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Home as HomeIcon, ShieldCheck } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'sturent_admin_token';

const TABS = [
  { key: 'pending', label: 'Pending review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'paused', label: 'Paused' },
  { key: 'all', label: 'All' },
];

const AdminListings = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState(token);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');

  const load = async (filt = tab, t = token) => {
    if (!t) return;
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/api/admin/listings?filter=${filt}`, {
        headers: { 'X-Admin-Token': t },
      });
      setItems(res.data.listings || []);
    } catch (e) {
      if (e.response?.status === 401) setErr('Invalid admin token.');
      else setErr('Could not load listings.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(tab, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, token]);

  const saveToken = () => {
    localStorage.setItem(TOKEN_KEY, tokenInput);
    setToken(tokenInput);
    toast.success('Admin token saved.');
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background" data-testid="admin-listings-gate">
        <AppHeader />
        <main className="max-w-md mx-auto px-5 py-20">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-5 space-y-3">
              <h1 className="font-heading font-bold text-lg text-slate-900">Admin access</h1>
              <p className="text-xs text-slate-600">Enter your admin token to review listings.</p>
              <Input
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="X-Admin-Token"
                className="h-11"
                data-testid="admin-token-input"
              />
              <Button className="w-full btn-primary" onClick={saveToken} disabled={!tokenInput} data-testid="admin-token-submit">
                Continue
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-listings-page">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading font-extrabold text-slate-900">Admin — listings</h1>
          <button
            type="button"
            onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(''); setTokenInput(''); }}
            className="text-xs text-slate-500 hover:text-slate-800 underline"
            data-testid="admin-logout"
          >
            Forget token
          </button>
        </div>
        <div className="flex gap-1 overflow-x-auto scrollbar-thin" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 rounded-full text-xs font-medium flex-shrink-0 ${
                tab === t.key ? 'bg-amber-500 text-white' : 'bg-white border border-slate-200 text-slate-700'
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {err && <p className="text-sm text-rose-600" data-testid="admin-error">{err}</p>}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
        ) : items.length === 0 ? (
          <Card className="bg-white border-slate-200"><CardContent className="p-8 text-center text-sm text-slate-500">
            No listings here.
          </CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="admin-listings-rows">
            {items.map((l) => {
              const cover = (l.photos || [])[0];
              const coverSrc = cover?.signed_token ? `${API}/api/files/${cover.signed_token}` : null;
              return (
                <Link
                  key={l.listing_id}
                  to={`/admin/listings/${l.listing_id}/review`}
                  className="block"
                  data-testid={`admin-row-${l.listing_id}`}
                >
                  <Card className="bg-white border-slate-200 hover:border-amber-300">
                    <CardContent className="p-3 flex gap-3 items-center">
                      <div className="h-14 w-20 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {coverSrc ? <img src={coverSrc} alt="" className="w-full h-full object-cover" /> : <HomeIcon className="h-6 w-6 m-auto text-slate-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{l.title || 'Untitled'}</p>
                        <p className="text-xs text-slate-500 truncate">{l.landlord_name || l.landlord_email} · {l.city || '—'}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-heading font-bold text-slate-900">${(l.total_monthly_cost_cad || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-slate-500 uppercase">{l.status?.replace(/_/g, ' ')}</div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminListings;
